/**
 * Set dressing at both tiers (integration plan slice 2b; lifted from the
 * dungeon-floors prototype's `dungeon-dressing.js` and the "Dressing" rules of
 * its `docs/THEME-KITS.md`): a worked mine yard of crates, barrels, lumber,
 * tools, wheelbarrows, signature mine carts on sidings, rope fences, rubble,
 * tufts and crystals. Everything comes from the floor, the theme's
 * `propRules.dressing*` fields and `floor seed + dressingSeedOffset` with tile
 * keys; no theme or floor has its own code path.
 *
 * Where props go (each with its own clearance):
 * 1. wall bands: a floor cell beside a cardinal wall owns a slot, the strip
 *    between the wall face and the swept clearance of every legal step;
 *    clusters of one to four kits along the wall, corners and back walls first;
 *    camera-side walls take only low kits; each rail room first gets a
 *    signature cart on a back wall;
 * 2. foreground ledges: low stubs directly south of room floor;
 * 3. fillers at free wall slots; 4. ground scatter near tile corners of rooms;
 * 5. terrace tops of tall rear rock (and camera-side foreground silhouettes);
 * 6. foreground kits on the camera-side border apron (outside the grid).
 * Every piece is refused on walls, markers and units, low supports and
 * rails, every candidate torch site, other dressing and legal movement edges.
 *
 * Tiers (the prototype's preset thinning, by instance order, never a
 * rebuild): each room's first cluster, then the rest, then fillers; tier 0 is
 * drawn at Off and Medium, tiers 1 and 2 at Medium only (the game's Medium is
 * the prototype's High and Balanced). Pieces go into the timber and iron box
 * batches, and four shape batches: rock (low-poly icosahedra), timber and iron
 * rounds (octagonal staves and bands) and tufts. The kit shapes' numbers are
 * the lifted generator's own arithmetic; its knobs are in `constants.ts`.
 */
import type { Theme } from '@ab/content-schema';
import { type Floor, type GridView, type Unit, canStep, dirOffset, dirs } from '@ab/game-core';
import * as THREE from 'three';
import { type Part, part } from './batch.ts';
import { presentation } from './constants.ts';
import type { WallRecord } from './floor-geometry.ts';
import { isOpen, roomAt, tileIndex, tileRandom } from './grid.ts';
import { type Bounds, backWallCells } from './lighting-plan.ts';
import type { Palette } from './palette.ts';

export const dressingBatches = ['timber', 'iron', 'rock', 'timberRound', 'ironRound', 'foliage'] as const;
export type DressingBatch = (typeof dressingBatches)[number];

type Kit = 'crates' | 'barrels' | 'lumber' | 'tools' | 'wheelbarrow' | 'cart' | 'fence' | 'rubble' | 'tufts' | 'crystals';
type ExtraKit = 'pebbles' | 'scrap' | 'ore' | 'sprig' | 'shards' | 'plateauBrush' | 'plateauRocks' | 'plateauCrate' | 'plateauBarrel' | 'fgCrates' | 'fgLumber' | 'fgRocks';
type AnyKit = Kit | ExtraKit;

type Piece = { readonly batch: DressingBatch; readonly p: THREE.Vector3; readonly s: readonly [number, number, number]; readonly q: THREE.Quaternion; readonly tag: string; readonly tint: THREE.Color };
type Colors = { readonly timber: THREE.Color; readonly iron: THREE.Color; readonly rock: THREE.Color; readonly ore: THREE.Color; readonly foliage: THREE.Color; readonly rope: THREE.Color; readonly crystal: THREE.Color; readonly steel: THREE.Color };
type Builder = { readonly pieces: Piece[]; readonly rand: () => number; readonly colors: Colors; low: boolean; onRock: boolean };
type Spec = { readonly batch: DressingBatch; readonly tile: number; readonly x: number; readonly y: number; readonly z: number; readonly s: readonly [number, number, number]; readonly rx: number; readonly ry: number; readonly rz: number; readonly tint: THREE.Color; readonly tag: string; readonly cutaway: number };
type Placed = { readonly kit: AnyKit; readonly width: number; readonly specs: Spec[]; readonly bounds: Bounds };
type Slot = {
  readonly x: number;
  readonly y: number;
  readonly nx: number;
  readonly nz: number;
  readonly tx: number;
  readonly tz: number;
  readonly room: number;
  readonly leftWall?: boolean;
  readonly rightWall?: boolean;
  readonly south?: boolean;
  readonly north?: boolean;
  readonly ledge?: boolean;
  readonly plateau?: boolean;
  readonly scatter?: boolean;
  readonly stub?: boolean;
  readonly maxHeight?: number;
  readonly fx?: number;
  readonly fz?: number;
  readonly fogTile?: number;
  readonly key: number;
  readonly rank: number;
  used?: boolean;
};
type Cluster = { readonly kits: readonly AnyKit[]; readonly room: number; readonly filler: boolean; readonly ledge: boolean; readonly plateau: boolean; readonly x: number; readonly z: number; readonly placed: readonly Placed[]; readonly triangles: number; tier: number };

export type DressingInput = {
  readonly floor: Floor;
  readonly units: readonly Unit[];
  readonly seed: number;
  readonly theme: Theme;
  readonly palette: Palette;
  readonly walls: readonly WallRecord[];
  /** Low supports (frame posts and braces) and rail ties that props keep clear of. */
  readonly structures: readonly (Bounds & { readonly post?: boolean; readonly rail?: boolean })[];
  /** Rock top at a world point, or null (seats ledge and terrace props). */
  readonly topAt: (x: number, z: number) => number | null;
};

export type DressingParts = {
  readonly parts: Readonly<Record<DressingBatch, readonly Part[]>>;
  /** Each placed cluster's footprint above the fixture band (fixtures keep clear of them). */
  readonly bounds: readonly Bounds[];
  /** Stub walls the ledge pass may use: no rock shoulder goes there (Off). */
  readonly ledgeWalls: ReadonlySet<number>;
  readonly counts: { readonly clusters: number; readonly pieces: number; readonly triangles: readonly [number, number, number] };
};

const D = presentation.dressing;
const MAIN_KITS: readonly Kit[] = ['crates', 'barrels', 'lumber', 'tools', 'wheelbarrow', 'cart', 'fence', 'crystals'];
const FILLER_KITS: readonly Kit[] = ['rubble', 'tufts', 'crystals'];
/** Kits low enough for the camera-side (south) walls, and kits that may stand on the low camera-side stubs. */
const LOW_KITS = new Set<AnyKit>(['lumber', 'fence', 'rubble', 'tufts', 'crystals']);
const LEDGE_KITS = new Set<AnyKit>(['crates', 'barrels', 'lumber', 'wheelbarrow', 'rubble', 'tufts', 'crystals']);
const WALL_GAP = D.wallGap;

// ---------- shared shape geometry ----------
/** An octagonal lathe with flats on the local axes; rings are [y, radius] in a unit box. */
const prismGeometry = (rings: readonly (readonly [number, number])[]): THREE.BufferGeometry => {
  const positions: number[] = [];
  const sides = 8;
  const angle = (k: number): number => Math.PI / 8 + (k * Math.PI) / 4;
  const point = (y: number, r: number, k: number): number[] => [Math.cos(angle(k)) * r, y, Math.sin(angle(k)) * r];
  const tri = (a: number[], b: number[], c: number[]): void => {
    positions.push(...a, ...b, ...c);
  };
  for (let i = 0; i < rings.length - 1; i += 1) {
    const [y0, r0] = rings[i] ?? [0, 0];
    const [y1, r1] = rings[i + 1] ?? [0, 0];
    for (let k = 0; k < sides; k += 1) {
      const a = point(y0, r0, k);
      const b = point(y0, r0, k + 1);
      const c = point(y1, r1, k + 1);
      const d = point(y1, r1, k);
      tri(a, d, c);
      tri(a, c, b);
    }
  }
  const first = rings[0] ?? [0, 0];
  const last = rings[rings.length - 1] ?? [0, 0];
  for (const [y, r, up] of [[first[0], first[1], false], [last[0], last[1], true]] as const) {
    for (let k = 1; k < sides - 1; k += 1) {
      const a = point(y, r, 0);
      const b = point(y, r, k);
      const c = point(y, r, k + 1);
      if (up) tri(a, c, b);
      else tri(a, b, c);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setIndex(Array.from({ length: positions.length / 3 }, (_, i) => i));
  g.computeVertexNormals();
  return g;
};
export const createBarrelGeometry = (): THREE.BufferGeometry => prismGeometry([[-0.5, 0.43], [0, 0.5], [0.5, 0.43]]);
export const createBandGeometry = (): THREE.BufferGeometry => prismGeometry([[-0.5, 0.5], [0.5, 0.5]]);
/** Seven tapered blades, each a back-to-back pair of triangles, darker at the base. */
export const createTuftGeometry = (): THREE.BufferGeometry => {
  const positions: number[] = [];
  const colors: number[] = [];
  for (let i = 0; i < 7; i += 1) {
    const a = (i * 2 * Math.PI) / 7 + (i % 2) * 0.4;
    const lean = 0.26 + (i % 3) * 0.07;
    const height = 0.62 + (((i * 5) % 7) / 7) * 0.38;
    const cx = Math.cos(a);
    const cz = Math.sin(a);
    const px = -cz;
    const pz = cx;
    const base = 0.07;
    const half = 0.075;
    const b1 = [cx * base + px * half, -0.5, cz * base + pz * half];
    const b2 = [cx * base - px * half, -0.5, cz * base - pz * half];
    const tip = [cx * (base + lean), -0.5 + height, cz * (base + lean)];
    for (const [p, q] of [
      [b1, b2],
      [b2, b1],
    ] as const) {
      positions.push(...p, ...q, ...tip);
      colors.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 1.05, 1.05, 1.05);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  g.setIndex(Array.from({ length: positions.length / 3 }, (_, i) => i));
  g.computeVertexNormals();
  return g;
};
/** Loose rock: a 20-triangle icosahedron in a unit box. */
export const createPebbleGeometry = (): THREE.BufferGeometry => new THREE.IcosahedronGeometry(0.5, 0);

// ---------- colour and pose helpers ----------
const shade = (base: THREE.Color, amount: number, seedValue: number): THREE.Color => base.clone().multiplyScalar(1 + (seedValue - 0.5) * 2 * amount);
const euler = new THREE.Euler(0, 0, 0, 'YXZ');
const quat = (rx = 0, ry = 0, rz = 0): THREE.Quaternion => new THREE.Quaternion().setFromEuler(euler.set(rx, ry, rz, 'YXZ'));
const put = (k: Builder, batch: DressingBatch, p: readonly number[], s: readonly [number, number, number], tag: string, tint: THREE.Color, q: THREE.Quaternion = new THREE.Quaternion()): void => {
  k.pieces.push({ batch, p: new THREE.Vector3(p[0], p[1], p[2]), s, q, tag, tint });
};
/** A child piece in a parent pose (tool heads, wheel forks). */
const child = (parent: { p: THREE.Vector3; q: THREE.Quaternion }, offset: readonly number[], q: THREE.Quaternion = new THREE.Quaternion()): { p: THREE.Vector3; q: THREE.Quaternion } => ({
  p: new THREE.Vector3(offset[0], offset[1], offset[2]).applyQuaternion(parent.q).add(parent.p),
  q: parent.q.clone().multiply(q),
});
/** Hoops share their barrel's yaw so octagon flats stay parallel. */
const lastBarrelQuat = (k: Builder): THREE.Quaternion => {
  for (let i = k.pieces.length - 1; i >= 0; i -= 1) {
    const p = k.pieces[i];
    if (p?.batch === 'timberRound') return p.q.clone();
  }
  return new THREE.Quaternion();
};

// ---------- kits (local frame: u along the wall, h up, v into the room) ----------
const crate = (k: Builder, u: number, base: number, w: number, h: number, d: number, yaw = 0): void => {
  const tint = shade(k.colors.timber, 0.1, k.rand());
  const frame = shade(k.colors.timber, 0.08, k.rand());
  const cu = u + w / 2;
  const q = quat(0, yaw, 0);
  const at = (du: number, dh: number, dv: number): number[] => {
    const p = new THREE.Vector3(du, dh, dv).applyQuaternion(q);
    return [cu + p.x, base + h / 2 + p.y, WALL_GAP + d / 2 + p.z];
  };
  const front = d / 2;
  put(k, 'timber', at(0, 0, -0.009), [w - 0.012, h - 0.012, d - 0.018], 'crate-body', tint, q);
  for (const side of [-1, 1]) put(k, 'timber', at(side * (w / 2 - 0.024), 0, front - 0.012), [0.048, h, 0.024], 'crate-stile', frame, q);
  for (const side of [-1, 1]) put(k, 'timber', at(0, side * (h / 2 - 0.022), front - 0.012), [w - 0.096, 0.044, 0.024], 'crate-rail', frame, q);
  const iw = w - 0.096;
  const ih = h - 0.088;
  const len = Math.hypot(iw, ih) - 0.01;
  const a = Math.atan2(ih, iw);
  put(k, 'timber', at(0, 0, front - 0.016), [len, 0.038, 0.02], 'crate-brace', frame, q.clone().multiply(quat(0, 0, a)));
  put(k, 'timber', at(0, 0, front - 0.02), [len, 0.038, 0.02], 'crate-brace', frame, q.clone().multiply(quat(0, 0, -a)));
  put(k, 'timber', at(0, h / 2 - 0.001, -0.009), [w - 0.03, 0.014, d - 0.05], 'crate-lid', shade(k.colors.timber, 0.12, k.rand()), q);
};
const hoops = (k: Builder, u: number, r: number, h: number, y0: number, q: THREE.Quaternion | null, v: number, thick: number): void => {
  for (const y of [0.22, 0.78]) {
    const profile = 0.5 - (0.07 * Math.abs(y - 0.5)) / 0.5;
    const scale = (profile + 0.012) / 0.5;
    if (q === null) put(k, 'ironRound', [u + r, h * y, v], [2 * r * scale, thick, 2 * r * scale], 'barrel-hoop', k.colors.iron, lastBarrelQuat(k));
    else put(k, 'ironRound', [u + h * y, y0, v], [2 * r * scale, thick, 2 * r * scale], 'barrel-hoop', k.colors.iron, q.clone());
  }
};
const barrelStanding = (k: Builder, x: number, v: number, r: number, h: number): void => {
  put(k, 'timberRound', [x, h / 2, v], [2 * r, h, 2 * r], 'barrel', shade(k.colors.timber, 0.1, k.rand()), quat(0, k.rand() * 0.7, 0));
  hoops(k, x - r, r, h, 0, null, v, 0.034);
};

const KITS: Readonly<Record<AnyKit, (k: Builder) => number>> = {
  crates(k) {
    const r = k.rand();
    const d = 0.236;
    const j = (): number => (k.rand() - 0.5) * 0.7;
    if (r < 0.16) {
      crate(k, 0, 0, 0.44, 0.43, d);
      return 0.44;
    }
    if (r < 0.38) {
      crate(k, 0, 0, 0.44, 0.43, d);
      crate(k, 0.47, 0, 0.36, 0.36, d - 0.02, j());
      return 0.83;
    }
    if (r < 0.66) {
      crate(k, 0, 0, 0.44, 0.42, d);
      crate(k, 0.47, 0, 0.38, 0.38, d - 0.01);
      crate(k, 0.04, 0.42, 0.38, 0.35, d - 0.03, j());
      return 0.85;
    }
    if (r < 0.86) {
      crate(k, 0, 0, 0.44, 0.42, d);
      crate(k, 0.02, 0.42, 0.4, 0.37, d - 0.02, j());
      crate(k, 0.05, 0.79, 0.34, 0.31, d - 0.04, j());
      return 0.44;
    }
    crate(k, 0, 0, 0.46, 0.44, d);
    crate(k, 0.49, 0, 0.4, 0.4, d - 0.01);
    crate(k, 0.03, 0.44, 0.4, 0.36, d - 0.03, j());
    crate(k, 0.52, 0.4, 0.34, 0.32, d - 0.04, j());
    crate(k, 0.06, 0.8, 0.32, 0.28, d - 0.05, j());
    return 0.89;
  },
  barrels(k) {
    const n = 1 + Math.floor(k.rand() * 3);
    const lying = n < 3 && k.rand() < 0.35;
    let u = 0;
    for (let i = 0; i < n; i += 1) {
      const r = 0.119 + k.rand() * 0.007;
      const h = 0.5 + k.rand() * 0.07;
      barrelStanding(k, u + r, WALL_GAP + r, r, h);
      u += 2 * r + 0.02;
    }
    if (lying) {
      const r = 0.112;
      const len = 0.36;
      const cy = r * 0.924;
      const q = quat(0, 0, Math.PI / 2);
      put(k, 'timberRound', [u + len / 2, cy, WALL_GAP + r], [2 * r, len, 2 * r], 'barrel', shade(k.colors.timber, 0.1, k.rand()), q);
      hoops(k, u, r, len, cy, q, WALL_GAP + r, 0.032);
      u += len + 0.02;
    }
    return u - 0.02;
  },
  lumber(k) {
    const len = 0.72 + k.rand() * 0.16;
    const tints = (): THREE.Color => shade(k.colors.timber, 0.14, k.rand());
    // Three staggered courses of planks along the wall, the bottom one on the floor.
    const courses = [[0.045, 0.115, 0.185], [0.08, 0.15], [0.115]];
    courses.forEach((row, level) => {
      for (const v of row) {
        const l = len - 0.02 - k.rand() * 0.08;
        put(k, 'timber', [len / 2 + (k.rand() - 0.5) * (len - l - 0.012), 0.02 + level * 0.038, v], [l, 0.04, 0.066], 'lumber-plank', tints(), quat(0, (k.rand() - 0.5) * 0.02, 0));
      }
    });
    // Planks leaning on the wall: foot out in the band, top resting just proud of the face. Nothing leans on a low wall.
    const leaning = k.low ? 0 : 1 + (k.rand() < 0.5 ? 1 : 0);
    for (let i = 0; i < leaning; i += 1) {
      const h = 0.62 + k.rand() * 0.3;
      const foot = 0.2 + k.rand() * 0.03;
      const top = 0.04;
      const lean = -Math.asin((foot - top) / h);
      const wide = k.rand() < 0.75;
      put(k, 'timber', [len + 0.07 + i * 0.1, (Math.cos(lean) * h) / 2, (foot + top) / 2], [wide ? 0.1 : 0.045, h, wide ? 0.026 : 0.045], 'lumber-leaning', tints(), quat(lean, 0, (k.rand() - 0.5) * 0.16));
    }
    return leaning ? len + 0.12 + (leaning - 1) * 0.1 : len;
  },
  tools(k) {
    const handle = shade(k.colors.timber, 0.1, k.rand());
    const steel = k.colors.steel;
    let u = 0.14;
    {
      // A pickaxe: handle foot on the floor, head resting against the wall.
      const len = 0.78;
      const q = quat(-0.14, 0, (k.rand() - 0.5) * 0.24);
      const pose = { p: new THREE.Vector3(u, 0, 0.135).add(new THREE.Vector3(0, len / 2, 0).applyQuaternion(q)), q };
      put(k, 'timber', pose.p.toArray(), [0.028, len, 0.028], 'pickaxe-handle', handle, q);
      for (const side of [-1, 1]) {
        const head = child(pose, [side * 0.055, len / 2 - 0.01, 0], quat(0, 0, side * 0.28));
        put(k, 'iron', head.p.toArray(), [0.14, 0.034, 0.032], 'pickaxe-head', steel, head.q);
      }
      u += 0.15;
    }
    if (k.rand() < 0.75) {
      // A shovel: blade on the floor, T grip at the top.
      const len = 0.66;
      const q = quat(-0.13, 0, (k.rand() - 0.5) * 0.2);
      const foot = new THREE.Vector3(u, 0.08, 0.13);
      const pose = { p: foot.clone().add(new THREE.Vector3(0, len / 2 + 0.06, 0).applyQuaternion(q)), q };
      put(k, 'iron', foot.toArray(), [0.12, 0.15, 0.014], 'shovel-blade', steel, q);
      put(k, 'timber', pose.p.toArray(), [0.026, len, 0.026], 'shovel-handle', handle, q);
      const grip = child(pose, [0, len / 2, 0]);
      put(k, 'timber', grip.p.toArray(), [0.085, 0.024, 0.024], 'shovel-grip', handle, grip.q);
      u += 0.1;
    }
    return u + 0.02;
  },
  wheelbarrow(k) {
    const wood = shade(k.colors.timber, 0.1, k.rand());
    const tray = { p: new THREE.Vector3(0.36, 0.25, 0.128), q: quat(0, 0, -0.14) };
    put(k, 'timber', tray.p.toArray(), [0.36, 0.14, 0.21], 'wheelbarrow-tray', wood, tray.q);
    put(k, 'iron', child(tray, [0, 0.065, 0.1]).p.toArray(), [0.37, 0.022, 0.022], 'wheelbarrow-lip', k.colors.steel, tray.q);
    put(k, 'timberRound', [0.6, 0.09, 0.128], [0.18, 0.035, 0.18], 'wheelbarrow-wheel', shade(k.colors.timber, 0.1, k.rand()), quat(Math.PI / 2, 0, 0));
    put(k, 'ironRound', [0.6, 0.09, 0.128], [0.05, 0.06, 0.05], 'wheelbarrow-hub', k.colors.steel, quat(Math.PI / 2, 0, 0));
    for (const v of [0.095, 0.161]) put(k, 'timber', [0.545, 0.15, v], [0.18, 0.026, 0.026], 'wheelbarrow-fork', wood, quat(0, 0, -0.72));
    for (const v of [0.035, 0.221]) {
      put(k, 'timber', [0.11, 0.255, v], [0.26, 0.028, 0.028], 'wheelbarrow-handle', wood, quat(0, 0, 0.12));
      put(k, 'timber', [0.23, 0.09, v], [0.026, 0.18, 0.026], 'wheelbarrow-leg', wood);
    }
    return 0.68;
  },
  cart(k) {
    // A mine cart on a short siding: ties and rails along the wall, a chassis, a flared tub, wheels and heaped ore.
    const body = k.colors.iron.clone().lerp(k.colors.steel, 0.5).multiplyScalar(0.9 + k.rand() * 0.15);
    const wood = shade(k.colors.timber, 0.1, k.rand());
    const steel = k.colors.steel;
    const len = 0.92;
    const cu = len / 2;
    const cv = 0.135;
    for (const u of [0.07, cu, len - 0.07]) put(k, 'timber', [u, 0.0125, cv], [0.075, 0.025, 0.26], 'cart-tie', shade(k.colors.timber, 0.12, k.rand()));
    for (const v of [0.065, 0.205]) put(k, 'iron', [cu, 0.04, v], [len, 0.03, 0.026], 'cart-rail', steel);
    put(k, 'iron', [cu, 0.1, cv], [0.5, 0.05, 0.19], 'cart-chassis', steel);
    for (const u of [cu - 0.18, cu + 0.18]) put(k, 'ironRound', [u, 0.088, 0.238], [0.155, 0.02, 0.155], 'cart-wheel', steel, quat(Math.PI / 2, 0, 0));
    put(k, 'iron', [cu, 0.225, cv], [0.48, 0.2, 0.2], 'cart-body', body);
    for (const v of [0.02, 0.245]) put(k, 'iron', [cu, 0.338, v], [0.56, 0.034, 0.026], 'cart-rim', steel);
    for (const u of [cu - 0.265, cu + 0.265]) put(k, 'iron', [u, 0.338, cv], [0.03, 0.034, 0.21], 'cart-rim', steel);
    for (const h of [0.2, 0.28]) put(k, 'timber', [cu, h, 0.241], [0.46, 0.036, 0.012], 'cart-board', wood);
    const n = 3 + (k.rand() < 0.5 ? 1 : 0);
    for (let i = 0; i < n; i += 1) {
      const s = 0.085 + k.rand() * 0.045;
      put(k, 'rock', [cu - 0.16 + (i * 0.32) / (n - 1), 0.33 + s * 0.18, 0.1 + k.rand() * 0.07], [s, s * 0.8, s], 'cart-ore', shade(k.colors.ore, 0.14, k.rand()), quat(k.rand(), k.rand() * 3, k.rand()));
    }
    return len;
  },
  fence(k) {
    const spans = 1 + (k.rand() < 0.5 ? 1 : 0);
    const step = 0.46;
    for (let i = 0; i <= spans; i += 1) put(k, 'timber', [0.025 + i * step, 0.21, 0.11], [0.055, 0.42, 0.055], 'fence-post', shade(k.colors.timber, 0.1, k.rand()));
    for (let i = 0; i < spans; i += 1) {
      for (const side of [0, 1]) {
        // Each span sags in two straight halves that meet below the post tops.
        const half = step / 2;
        const sag = 0.045;
        const ang = Math.atan2(sag, half);
        put(k, 'timber', [0.025 + i * step + half / 2 + side * half, 0.36 - sag / 2, 0.11], [Math.hypot(half, sag) + 0.01, 0.014, 0.014], 'fence-rope', k.colors.rope, quat(0, 0, side ? ang : -ang));
      }
    }
    return 0.05 + spans * step;
  },
  rubble(k) {
    const n = 3 + Math.floor(k.rand() * 3);
    let u = 0;
    for (let i = 0; i < n; i += 1) {
      const s = 0.05 + k.rand() * 0.075;
      const r = 0.72 * s * 1.2;
      put(k, 'rock', [u + r, s * 0.28, 0.015 + r + k.rand() * 0.08], [s, s * 0.75, s * (0.8 + k.rand() * 0.4)], 'rubble', shade(k.colors.rock, 0.16, k.rand()), quat(k.rand(), k.rand() * 6, k.rand()));
      u += r * 1.3;
    }
    return u + 0.03;
  },
  tufts(k) {
    // Tufts on rock are dry brush, never bright on the dark caps.
    const foliage = k.onRock ? k.colors.foliage.clone().lerp(k.colors.rock, 0.5).multiplyScalar(0.6) : k.colors.foliage;
    const n = 1 + Math.floor(k.rand() * 3);
    let u = 0;
    for (let i = 0; i < n; i += 1) {
      const w = 0.15 + k.rand() * 0.09;
      const h = 0.13 + k.rand() * 0.1;
      put(k, 'foliage', [u + w / 2, h / 2, 0.015 + w / 2 + k.rand() * 0.05], [w, h, w], 'tuft', shade(foliage, 0.14, k.rand()), quat(0, k.rand() * 6.3, 0));
      u += w * 0.85;
    }
    return u + 0.03;
  },
  crystals(k) {
    const n = 3 + Math.floor(k.rand() * 3);
    let u = 0;
    for (let i = 0; i < n; i += 1) {
      const h = 0.16 + k.rand() * 0.18;
      const w = 0.06 + k.rand() * 0.035;
      const reach = 0.72 * (w + h * 0.25);
      put(k, 'rock', [u + reach, h * 0.38, 0.015 + reach + k.rand() * 0.03], [w, h, w], 'crystal', shade(k.colors.crystal, 0.14, k.rand()), quat((k.rand() - 0.5) * 0.45, k.rand() * 6, (k.rand() - 0.5) * 0.45));
      u += reach * 1.2;
    }
    return u + 0.04;
  },
  // Ground scatter: centred on its anchor, under ankle height.
  pebbles(k) {
    const n = 2 + Math.floor(k.rand() * 3);
    for (let i = 0; i < n; i += 1) {
      const s = 0.028 + k.rand() * 0.035;
      const a = k.rand() * 6.28;
      const d = k.rand() * 0.08;
      put(k, 'rock', [Math.cos(a) * d, s * 0.22, Math.sin(a) * d], [s, s * 0.7, s * (0.8 + k.rand() * 0.4)], 'pebble', shade(k.colors.rock, 0.2, k.rand()), quat(k.rand(), k.rand() * 6, k.rand()));
    }
    return 0;
  },
  scrap(k) {
    // Loose planks dropped at odd angles, sometimes crossed.
    const n = 1 + (k.rand() < 0.55 ? 1 : 0) + (k.rand() < 0.2 ? 1 : 0);
    const yaw = k.rand() * 3.14;
    for (let i = 0; i < n; i += 1) put(k, 'timber', [(k.rand() - 0.5) * 0.08, 0.009 + i * 0.018, (k.rand() - 0.5) * 0.08], [0.2 + k.rand() * 0.12, 0.018, 0.05], 'scrap', shade(k.colors.timber, 0.15, k.rand()), quat(0, yaw + (i ? (k.rand() - 0.5) * 1.4 : 0), 0));
    return 0;
  },
  ore(k) {
    const n = 3 + Math.floor(k.rand() * 4);
    for (let i = 0; i < n; i += 1) {
      const s = 0.022 + k.rand() * 0.026;
      const a = k.rand() * 6.28;
      const d = k.rand() * 0.1;
      put(k, 'rock', [Math.cos(a) * d, s * 0.15, Math.sin(a) * d], [s, s * 0.45, s * (0.8 + k.rand() * 0.4)], 'ore', shade(k.rand() < 0.5 ? k.colors.ore : k.colors.rock, 0.2, k.rand()), quat(0, k.rand() * 6, 0));
    }
    return 0;
  },
  sprig(k) {
    const w = 0.1 + k.rand() * 0.05;
    const h = 0.06 + k.rand() * 0.035;
    put(k, 'foliage', [0, h / 2, 0], [w, h, w], 'sprig', shade(k.colors.foliage, 0.14, k.rand()), quat(0, k.rand() * 6.3, 0));
    return 0;
  },
  shards(k) {
    const n = 2 + Math.floor(k.rand() * 2);
    for (let i = 0; i < n; i += 1) {
      const h = 0.06 + k.rand() * 0.045;
      const w = 0.032 + k.rand() * 0.018;
      put(k, 'rock', [(k.rand() - 0.5) * 0.08, h * 0.35, (k.rand() - 0.5) * 0.08], [w, h, w], 'shard', shade(k.colors.crystal, 0.14, k.rand()), quat((k.rand() - 0.5) * 0.5, k.rand() * 6, (k.rand() - 0.5) * 0.5));
    }
    return 0;
  },
  // Terrace tops: cheap brush, loose rock, the odd crate or barrel.
  plateauBrush(k) {
    const n = 2 + Math.floor(k.rand() * 3);
    const dry = k.colors.foliage.clone().lerp(k.colors.rock, 0.5);
    for (let i = 0; i < n; i += 1) {
      const w = 0.2 + k.rand() * 0.14;
      const h = 0.16 + k.rand() * 0.16;
      const a = k.rand() * 6.28;
      const d = k.rand() * 0.16;
      put(k, 'foliage', [Math.cos(a) * d, h / 2, 0.13 + Math.sin(a) * d * 0.5], [w, h, w], 'plateau-brush', shade(shade(dry, 0.18, k.rand()), 0.5, 0), quat(0, k.rand() * 6.3, 0));
    }
    if (k.rand() < 0.5) {
      const s = 0.07 + k.rand() * 0.08;
      put(k, 'rock', [0.16, s * 0.3, 0.12], [s, s * 0.75, s], 'plateau-rock', shade(k.colors.rock, 0.16, k.rand()), quat(k.rand(), k.rand() * 6, k.rand()));
    }
    return 0;
  },
  plateauRocks(k) {
    const n = 2 + Math.floor(k.rand() * 3);
    for (let i = 0; i < n; i += 1) {
      const s = 0.07 + k.rand() * 0.1;
      const a = k.rand() * 6.28;
      const d = k.rand() * 0.14;
      put(k, 'rock', [Math.cos(a) * d, s * 0.3, 0.13 + Math.sin(a) * d * 0.5], [s, s * 0.75, s * (0.8 + k.rand() * 0.4)], 'plateau-rock', shade(k.colors.rock, 0.16, k.rand()), quat(k.rand(), k.rand() * 6, k.rand()));
    }
    return 0;
  },
  plateauCrate(k) {
    crate(k, -0.18, 0, 0.36, 0.34, 0.236, (k.rand() - 0.5) * 0.5);
    return 0;
  },
  plateauBarrel(k) {
    barrelStanding(k, 0, 0.02 + 0.12, 0.12, 0.5);
    return 0;
  },
  // Foreground silhouettes on camera-side terraces (blurred by the lens), under 0.6 tall.
  fgCrates(k) {
    const yaw = (k.rand() - 0.5) * 0.6;
    crate(k, -0.26, 0, 0.4, 0.3, 0.3, yaw);
    crate(k, 0.18, 0, 0.34, 0.28, 0.28, yaw + (k.rand() - 0.5) * 0.4);
    if (k.rand() < 0.6) crate(k, -0.2, 0.3, 0.3, 0.26, 0.26, yaw + (k.rand() - 0.5) * 0.5);
    return 0;
  },
  fgLumber(k) {
    const yaw = (k.rand() - 0.5) * 0.5;
    const q = quat(0, yaw, 0);
    const len = 0.7 + k.rand() * 0.2;
    [[-0.07, 0, 0.07], [-0.035, 0.035], [0]].forEach((row, level) => {
      for (const v of row) {
        const p = new THREE.Vector3(0, 0.02 + level * 0.04, v).applyQuaternion(q);
        put(k, 'timber', [p.x, p.y, 0.15 + p.z], [len - k.rand() * 0.12, 0.04, 0.066], 'fg-lumber', shade(k.colors.timber, 0.14, k.rand()), q);
      }
    });
    return 0;
  },
  fgRocks(k) {
    const n = 3 + Math.floor(k.rand() * 3);
    for (let i = 0; i < n; i += 1) {
      const s = 0.12 + k.rand() * 0.12;
      const a = k.rand() * 6.28;
      const d = k.rand() * 0.2;
      put(k, 'rock', [Math.cos(a) * d, s * 0.25, 0.15 + Math.sin(a) * d * 0.5], [s, s * 0.7, s * (0.8 + k.rand() * 0.4)], 'fg-rock', shade(k.colors.rock, 0.16, k.rand()), quat(k.rand(), k.rand() * 6, k.rand()));
    }
    return 0;
  },
};

// ---------- bounds ----------
const corner = new THREE.Vector3();
const rotation = new THREE.Quaternion();
/** The world-axis bounds of a piece: radial pieces as spheres or cylinders, boxes by their rotated corners. */
const pieceBounds = (s: Spec): Bounds => {
  const upright = Math.abs(s.rx) < 1e-6 && Math.abs(s.rz) < 1e-6;
  if (upright && (s.batch === 'timberRound' || s.batch === 'ironRound')) {
    const r = 0.5 * Math.max(s.s[0], s.s[2]);
    return { x0: s.x - r, x1: s.x + r, z0: s.z - r, z1: s.z + r, y0: s.y - s.s[1] / 2, y1: s.y + s.s[1] / 2 };
  }
  if (s.batch === 'rock' || s.batch === 'foliage') {
    const r = s.batch === 'rock' ? 0.72 * Math.max(...s.s) : 0.5 * Math.max(s.s[0], s.s[2]);
    const ry = s.batch === 'rock' ? r : s.s[1] / 2;
    return { x0: s.x - r, x1: s.x + r, z0: s.z - r, z1: s.z + r, y0: s.y - ry, y1: s.y + ry };
  }
  rotation.setFromEuler(euler.set(s.rx, s.ry, s.rz, 'YXZ'));
  const b = { x0: Infinity, x1: -Infinity, z0: Infinity, z1: -Infinity, y0: Infinity, y1: -Infinity };
  for (const cx of [-0.5, 0.5]) {
    for (const cy of [-0.5, 0.5]) {
      for (const cz of [-0.5, 0.5]) {
        corner.set(cx * s.s[0], cy * s.s[1], cz * s.s[2]).applyQuaternion(rotation);
        b.x0 = Math.min(b.x0, s.x + corner.x);
        b.x1 = Math.max(b.x1, s.x + corner.x);
        b.y0 = Math.min(b.y0, s.y + corner.y);
        b.y1 = Math.max(b.y1, s.y + corner.y);
        b.z0 = Math.min(b.z0, s.z + corner.z);
        b.z1 = Math.max(b.z1, s.z + corner.z);
      }
    }
  }
  return b;
};
const overlap = (a: Pick<Bounds, 'x0' | 'x1' | 'z0' | 'z1'>, b: Pick<Bounds, 'x0' | 'x1' | 'z0' | 'z1'>, pad: number): boolean => a.x0 < b.x1 + pad && a.x1 > b.x0 - pad && a.z0 < b.z1 + pad && a.z1 > b.z0 - pad;
const union = (list: readonly Bounds[]): Bounds => ({
  x0: Math.min(...list.map((b) => b.x0)),
  x1: Math.max(...list.map((b) => b.x1)),
  z0: Math.min(...list.map((b) => b.z0)),
  z1: Math.max(...list.map((b) => b.z1)),
  y0: Math.min(...list.map((b) => b.y0)),
  y1: Math.max(...list.map((b) => b.y1)),
});
/** Whether a segment passes within `r` of a box (2D, x and z). */
const segmentNear = (a: { x: number; z: number }, b: { x: number; z: number }, box: Bounds, r: number): boolean => {
  for (let n = 0; n <= D.segmentSamples; n += 1) {
    const t = n / D.segmentSamples;
    const x = a.x + (b.x - a.x) * t;
    const z = a.z + (b.z - a.z) * t;
    const dx = Math.max(box.x0 - x, 0, x - box.x1);
    const dz = Math.max(box.z0 - z, 0, z - box.z1);
    if (dx * dx + dz * dz < r * r) return true;
  }
  return false;
};
/** Spatial buckets of one tile, so placement never scans every prop. */
const buckets = <T>() => {
  const cells = new Map<number, T[]>();
  const key = (x: number, z: number): number => x * 4096 + z;
  return {
    insert(b: Pick<Bounds, 'x0' | 'x1' | 'z0' | 'z1'>, value: T): void {
      for (let x = Math.floor(b.x0); x <= Math.floor(b.x1); x += 1) {
        for (let z = Math.floor(b.z0); z <= Math.floor(b.z1); z += 1) {
          const list = cells.get(key(x, z));
          if (list) list.push(value);
          else cells.set(key(x, z), [value]);
        }
      }
    },
    any(b: Pick<Bounds, 'x0' | 'x1' | 'z0' | 'z1'>, pad: number, test: (value: T) => boolean): boolean {
      for (let x = Math.floor(b.x0 - pad); x <= Math.floor(b.x1 + pad); x += 1) {
        for (let z = Math.floor(b.z0 - pad); z <= Math.floor(b.z1 + pad); z += 1) {
          if (cells.get(key(x, z))?.some(test)) return true;
        }
      }
      return false;
    },
  };
};
const seeded = (start: number): (() => number) => {
  let state = Math.floor(start * 4294967296) >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
const pick = <K extends string>(weights: readonly (readonly [K, number])[], rand: () => number): K | null => {
  const total = weights.reduce((s, [, w]) => s + w, 0);
  if (total <= 0) return null;
  let r = rand() * total;
  for (const [name, w] of weights) {
    r -= w;
    if (r <= 0) return name;
  }
  return weights[weights.length - 1]?.[0] ?? null;
};

/** Every cell where the lighting plan may stand a fixture (door cells, corridor turns, room back corners and centre cells), reserved whether or not it gets one. */
const torchSites = (floor: Floor): Bounds[] => {
  const t = D.torchSite;
  const sites: Bounds[] = [];
  const site = (x: number, z: number): void => {
    sites.push({ x0: x - t.half, x1: x + t.half, z0: z - t.half, z1: z + t.half, y0: 0, y1: 1 });
  };
  const open = (x: number, y: number): boolean => isOpen(floor, x, y);
  const steps: readonly (readonly [number, number])[] = [
    [0, 1],
    [1, 0],
    [0, -1],
    [-1, 0],
  ];
  const order: readonly (readonly [number, number])[] = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];
  for (let y = 1; y < floor.height - 1; y += 1) {
    for (let x = 1; x < floor.width - 1; x += 1) {
      if (!open(x, y)) continue;
      const openDirs = steps.filter(([dx, dy]) => open(x + dx, y + dy));
      const wall = order.find(([dx, dy]) => !open(x + dx, y + dy));
      if (wall === undefined) continue;
      const room = roomAt(floor, x, y);
      const door = openDirs.some(([dx, dy]) => room >= 0 !== roomAt(floor, x + dx, y + dy) >= 0);
      const [o0, o1] = openDirs;
      const turn = room < 0 && openDirs.length === 2 && o0 !== undefined && o1 !== undefined && o0[0] !== -o1[0];
      if (door || turn) site(x + (wall[0] || 1) * t.site[0], -y - wall[1] * t.site[1]);
    }
  }
  for (const r of floor.rooms) for (const [x, side] of [[r.x, -1], [r.x + r.w - 1, 1]] as const) site(x + side * t.corner, -(r.y + r.h - 1));
  for (const r of floor.rooms) for (const x of backWallCells(floor, r).slice(0, r.w >= t.wideRoom ? 2 : 0)) site(x + t.site[0], -(r.y + r.h - 1) - t.site[1]);
  return sites;
};

/** Stub walls directly south of room floor that the ledge pass will try first (the rock shoulders stay off them). */
export const dressingLedgeCells = (floor: Floor, seed: number, theme: Theme, walls: readonly WallRecord[]): Set<number> => {
  const rules = theme.propRules;
  const density = rules.dressingDensityPercent / 100;
  const dressingSeed = (seed + rules.dressingSeedOffset) >>> 0;
  const out = new Set<number>();
  if (density <= 0 || rules.dressingMaxPieces <= 0) return out;
  const perRoom = new Map<number, { index: number; x: number; rank: number }[]>();
  for (const w of walls) {
    if (!w.cutaway || w.height >= D.ledgeWallMax || w.x <= 0 || w.x >= floor.width - 1 || floor.tiles[tileIndex(floor, w.x, w.y + 1)] !== 'floor') continue;
    const room = roomAt(floor, w.x, w.y + 1);
    const rank = tileRandom(dressingSeed, w.x, w.y, 73);
    if (room < 0 || rank > density) continue;
    const list = perRoom.get(room) ?? [];
    list.push({ index: w.index, x: w.x, rank });
    perRoom.set(room, list);
  }
  for (const [room, list] of perRoom) {
    const width = floor.rooms[room]?.w ?? 0;
    const target = Math.max(1, Math.min(3, Math.round((width * rules.dressingLedgePerTilePercent) / 100)));
    const taken: { x: number }[] = [];
    for (const c of list.sort((a, b) => a.rank - b.rank || a.index - b.index)) {
      if (taken.length >= target * 2) break;
      if (taken.some((t) => Math.abs(t.x - c.x) < 2)) continue;
      taken.push(c);
      out.add(c.index);
    }
  }
  return out;
};

export const buildDressing = (input: DressingInput): DressingParts => {
  const { floor, units, theme, palette, walls, structures, topAt } = input;
  const rules = theme.propRules;
  const density = rules.dressingDensityPercent / 100;
  const kitWeight = (kit: Kit): number => rules.dressingKit[kit] / 100;
  const dressingSeed = (input.seed + rules.dressingSeedOffset) >>> 0;
  const parts: Record<DressingBatch, Part[]> = { timber: [], iron: [], rock: [], timberRound: [], ironRound: [], foliage: [] };
  const ledgeWalls = dressingLedgeCells(floor, input.seed, theme, walls);
  const empty: DressingParts = { parts, bounds: [], ledgeWalls, counts: { clusters: 0, pieces: 0, triangles: [0, 0, 0] } };
  if (density <= 0 || rules.dressingMaxPieces <= 0) return empty;
  const colors: Colors = { timber: palette.timber, iron: palette.iron, rock: palette.wall, ore: palette.ore, foliage: palette.foliage, rope: palette.rope, crystal: palette.crystal, steel: palette.rail };
  const index = (x: number, y: number): number => tileIndex(floor, x, y);
  const tileAt = (x: number, y: number): string => (x >= 0 && y >= 0 && x < floor.width && y < floor.height ? (floor.tiles[index(x, y)] ?? 'wall') : 'wall');
  const f = D.feature;
  const features = [
    ...units.map((v) => ({ pos: v.pos, radius: v.boss !== null ? f.boss : f.unit })),
    ...floor.items.map((v) => ({ pos: v.pos, radius: f.item })),
    ...floor.springs.map((v) => ({ pos: v.pos, radius: f.spring })),
    ...floor.tiles.flatMap((t, i) => (t === 'exit' ? [{ pos: { x: i % floor.width, y: Math.floor(i / floor.width) }, radius: f.exit }] : [])),
  ];
  const featureBounds = features.map((v) => ({ x0: v.pos.x - v.radius, x1: v.pos.x + v.radius, z0: -v.pos.y - v.radius, z1: -v.pos.y + v.radius }));
  const sites = torchSites(floor);
  const structureGrid = buckets<Bounds & { post?: boolean; rail?: boolean }>();
  for (const b of structures) structureGrid.insert(b, b);
  for (const t of sites) structureGrid.insert(t, t);
  const routeGrid = buckets<{ a: { x: number; z: number }; b: { x: number; z: number } }>();
  const placedGrid = buckets<Bounds>();
  const grid: GridView = {
    width: floor.width,
    height: floor.height,
    isFloor: (p) => isOpen(floor, p.x, p.y),
    unitAt: () => null,
    roomAt: (p) => {
      const r = roomAt(floor, p.x, p.y);
      return r < 0 ? null : r;
    },
    roomTiles: () => [],
  };
  for (let y = 0; y < floor.height; y += 1) {
    for (let x = 0; x < floor.width; x += 1) {
      if (!isOpen(floor, x, y)) continue;
      for (const dir of dirs) {
        const d = dirOffset[dir];
        if (!canStep(grid, { x, y }, dir) || index(x + d.x, y + d.y) < index(x, y)) continue;
        const e = { a: { x, z: -y }, b: { x: x + d.x, z: -y - d.y } };
        routeGrid.insert({ x0: Math.min(e.a.x, e.b.x), x1: Math.max(e.a.x, e.b.x), z0: Math.min(e.a.z, e.b.z), z1: Math.max(e.a.z, e.b.z) }, e);
      }
    }
  }
  // Wall-side slots: one per (floor cell, cardinal wall); n points at the wall.
  const slots: Slot[] = [];
  for (let y = 1; y < floor.height - 1; y += 1) {
    for (let x = 1; x < floor.width - 1; x += 1) {
      if (tileAt(x, y) !== 'floor') continue;
      for (const [dx, dy] of [
        [0, 1],
        [1, 0],
        [0, -1],
        [-1, 0],
      ] as const) {
        if (tileAt(x + dx, y + dy) !== 'wall') continue;
        const nx = dx;
        const nz = -dy;
        const tx = -nz;
        const tz = nx;
        slots.push({
          x,
          y,
          nx,
          nz,
          tx,
          tz,
          room: roomAt(floor, x, y),
          leftWall: tileAt(x - tx, y + tz) === 'wall',
          rightWall: tileAt(x + tx, y - tz) === 'wall',
          south: nz === 1,
          north: nz === -1,
          key: index(x, y) * 4 + (dx + 1) + (dy + 1) * 3,
          rank: tileRandom(dressingSeed, x, y, 67 + dx + dy * 3),
        });
      }
    }
  }
  // Foreground ledges: the tops of low stubs directly south of room floor.
  for (const i of ledgeWalls) {
    const x = i % floor.width;
    const y = Math.floor(i / floor.width);
    const room = tileAt(x, y + 1) === 'floor' ? roomAt(floor, x, y + 1) : -1;
    if (room < 0) continue;
    slots.push({ ledge: true, x, y, nx: 0, nz: -1, tx: 1, tz: 0, fx: x, fz: -y - D.ledgeInset, room, fogTile: index(x, y + 1), leftWall: !ledgeWalls.has(i - 1), rightWall: !ledgeWalls.has(i + 1), key: i * 4 + 3, rank: tileRandom(dressingSeed, x, y, 73) });
  }
  const stubWalls = new Set(walls.filter((w) => w.cutaway && w.height < D.ledgeWallMax && !isOpen(floor, w.x, w.y + 1)).map((w) => w.index));
  const near = (x: number, y: number): boolean => {
    for (let dy = -3; dy <= 3; dy += 1) for (let dx = -3; dx <= 3; dx += 1) if (isOpen(floor, x + dx, y + dy)) return true;
    return false;
  };
  const plateauWalls = new Set(
    walls
      .filter((w) => w.x > 0 && w.y > 0 && w.x < floor.width - 1 && w.y < floor.height - 1 && (w.cutaway ? w.height < D.ledgeWallMax && !isOpen(floor, w.x, w.y + 1) : near(w.x, w.y) || [-1, 0, 1].some((dx) => isOpen(floor, w.x + dx, w.y + 4))))
      .map((w) => w.index),
  );
  const nearSite = (s: Slot): boolean =>
    sites.some((t) => Math.abs((t.x0 + t.x1) / 2 - s.x) + Math.abs((t.z0 + t.z1) / 2 + s.y) < D.nearSite) ||
    structures.some((b) => b.post === true && Math.abs((b.x0 + b.x1) / 2 - s.x) + Math.abs((b.z0 + b.z1) / 2 + s.y) < D.nearPost);
  const railRooms = new Set(structures.filter((b) => b.rail === true).map((b) => roomAt(floor, Math.round((b.x0 + b.x1) / 2), Math.round(-(b.z0 + b.z1) / 2))).filter((r) => r >= 0));
  const railPoints = structures.filter((b) => b.rail === true).map((b) => ({ x: (b.x0 + b.x1) / 2, z: (b.z0 + b.z1) / 2 }));
  const clusters: Cluster[] = [];
  let pieces = 0;

  const fits = (b: Bounds, cluster: readonly Placed[], slot: Slot): boolean => {
    const c = D.clearance;
    const inner = { x0: b.x0 + c.inner, x1: b.x1 - c.inner, z0: b.z0 + c.inner, z1: b.z1 - c.inner };
    for (let gx = Math.round(inner.x0); gx <= Math.round(inner.x1); gx += 1) {
      for (let gy = Math.round(-inner.z1); gy <= Math.round(-inner.z0); gy += 1) {
        const i = index(gx, gy);
        if (slot.plateau ? !plateauWalls.has(i) : slot.ledge ? !ledgeWalls.has(i) || gx <= 0 || gx >= floor.width - 1 : tileAt(gx, gy) !== 'floor') return false;
      }
    }
    if ((slot.ledge && b.y1 > D.ledgeMaxHeight) || (slot.stub && b.y1 > D.stubMaxHeight) || (slot.maxHeight !== undefined && b.y1 > slot.maxHeight)) return false;
    // Ground scatter stays off every tile centre (unit feet, blob shadows).
    if (slot.scatter && b.y1 > D.scatterMaxHeight) return false;
    if (slot.scatter) {
      for (let gx = Math.floor(b.x0); gx <= Math.ceil(b.x1); gx += 1) {
        for (let gz = Math.floor(b.z0); gz <= Math.ceil(b.z1); gz += 1) {
          if (Math.max(b.x0 - gx, gx - b.x1, 0) ** 2 + Math.max(b.z0 - gz, gz - b.z1, 0) ** 2 < D.scatterCentre ** 2) return false;
        }
      }
    }
    if (featureBounds.some((fb) => overlap(b, fb, c.feature))) return false;
    if (structureGrid.any(b, c.structure, (s) => overlap(b, s, c.structure))) return false;
    if (placedGrid.any(b, c.dressing, (s) => overlap(b, s, c.dressing))) return false;
    if (cluster.some((k) => overlap(b, k.bounds, c.cluster))) return false;
    if (!slot.scatter && !slot.plateau && routeGrid.any(b, 1, (e) => segmentNear(e.a, e.b, b, c.route))) return false;
    return true;
  };
  const realize = (slot: Slot, kit: AnyKit, rand: () => number, uStart: number, dir: number): Placed => {
    const k: Builder = { pieces: [], rand, colors, low: slot.south === true || slot.ledge === true || slot.plateau === true, onRock: slot.ledge === true || slot.plateau === true };
    const width = KITS[kit](k);
    const yaw = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.atan2(-slot.tz, slot.tx));
    const fx = slot.fx ?? slot.x + slot.nx * 0.5;
    const fz = slot.fz ?? -slot.y + slot.nz * 0.5;
    const specs: Spec[] = k.pieces.map((piece) => {
      let u = piece.p.x;
      let q = piece.q;
      if (dir < 0) {
        u = -u;
        q = new THREE.Quaternion(q.x, -q.y, -q.z, q.w);
      }
      u += uStart;
      const v = piece.p.z;
      const e = euler.setFromQuaternion(yaw.clone().multiply(q), 'YXZ');
      return { batch: piece.batch, tile: slot.fogTile ?? index(slot.x, slot.y), x: fx + slot.tx * u - slot.nx * v, y: piece.p.y, z: fz + slot.tz * u - slot.nz * v, s: piece.s, rx: e.x, ry: e.y, rz: e.z, tint: piece.tint, tag: piece.tag, cutaway: piece.batch === 'rock' || piece.batch === 'foliage' ? 0 : 1 };
    });
    return { kit, width, specs, bounds: union(specs.map(pieceBounds)) };
  };
  const tryCluster = (slot: Slot, fillerOnly: boolean, forced: Kit | null = null): Placed[] | null => {
    const rand = seeded(tileRandom(dressingSeed, slot.x, slot.y, 71 + (slot.key % 11)));
    const allowed = (name: Kit): boolean => (slot.ledge ? LEDGE_KITS.has(name) : slot.south !== true || LOW_KITS.has(name));
    // Carts come only as each rail room's signature cart; fences favour camera-side walls; crates and barrels corners.
    const context = (name: Kit): number => (name === 'cart' ? 0 : name === 'fence' ? (slot.south ? 3 : 0) : (name === 'crates' || name === 'barrels') && (slot.leftWall || slot.rightWall) ? 1.5 : 1);
    const main = forced ?? pick((fillerOnly ? FILLER_KITS : MAIN_KITS).filter(allowed).map((n) => [n, kitWeight(n) * (fillerOnly ? 1 : context(n))] as const), rand);
    if (main === null) return null;
    const kits: Kit[] = [main];
    if (!fillerOnly) {
      // Back walls face the camera: longer clusters there; a signature cart keeps its siding clear.
      const companions = forced === 'cart' ? 0 : 2 + (rand() < 0.5 ? 1 : 0) + (slot.north ? 1 : 0);
      for (let i = 0; i < companions; i += 1) {
        const c = pick(MAIN_KITS.filter((n) => allowed(n) && !kits.includes(n) && n !== 'cart' && n !== 'wheelbarrow' && n !== 'fence').map((n) => [n, kitWeight(n)] as const), rand);
        if (c !== null) kits.push(c);
      }
      if (rand() < D.fillerCompanion) {
        const c = pick(FILLER_KITS.filter((n) => !kits.includes(n) && !(forced !== null && n === 'tufts')).map((n) => [n, kitWeight(n)] as const), rand);
        if (c !== null) kits.push(c);
      }
    }
    // Start snug in a room corner when there is one.
    const dir = slot.leftWall ? 1 : slot.rightWall ? -1 : rand() < 0.5 ? 1 : -1;
    let cursor = (slot.leftWall || slot.rightWall ? 0.5 - WALL_GAP : 0.46 - rand() * 0.22) * -dir;
    const cluster: Placed[] = [];
    for (const kit of kits) {
      let placed: Placed | null = null;
      for (let attempt = 0; attempt < (cluster.length ? 2 : 3) && placed === null; attempt += 1) {
        const next = realize(slot, kit, rand, cursor, dir);
        if (fits(next.bounds, cluster, slot)) placed = next;
      }
      if (placed === null) continue;
      cluster.push(placed);
      cursor += dir * (placed.width + 0.035 + rand() * 0.05);
    }
    if (forced !== null && cluster[0]?.kit !== forced) return null;
    return cluster.length > 0 ? cluster : null;
  };
  const trianglesOf = (placed: readonly Placed[]): number => placed.reduce((n, k) => n + k.specs.reduce((m, s) => m + D.triangles[s.batch], 0), 0);
  const commit = (slot: Slot, placed: readonly Placed[], filler: boolean): boolean => {
    const count = placed.reduce((n, k) => n + k.specs.length, 0);
    if (pieces + count > rules.dressingMaxPieces) return false;
    const bounds = union(placed.map((k) => k.bounds));
    for (const k of placed) placedGrid.insert(k.bounds, k.bounds);
    clusters.push({ kits: placed.map((k) => k.kit), room: slot.room, filler, ledge: slot.ledge === true, plateau: slot.plateau === true, x: (bounds.x0 + bounds.x1) / 2, z: (bounds.z0 + bounds.z1) / 2, placed, triangles: trianglesOf(placed), tier: 0 });
    pieces += count;
    slot.used = true;
    return true;
  };

  const perTile = rules.dressingPerTilePercent / 100;
  const spacing = rules.dressingSpacingPercent / 100;
  const byRoom = floor.rooms.map((room, i) => ({
    list: slots
      .filter((s) => s.room === i && !s.ledge && s.rank <= density)
      .map((s) => ({ s, score: s.rank - (s.leftWall || s.rightWall ? 0.5 : 0) - (s.north ? 0.6 : 0) + (s.south ? 0.12 : 0) - (nearSite(s) ? 0.14 : 0) }))
      .sort((a, b) => a.score - b.score || a.s.key - b.s.key)
      .map((v) => v.s),
    target: Math.max(1, Math.min(rules.dressingRoomMax, Math.round(room.w * room.h * perTile))),
    placed: 0,
    cursor: 0,
  }));
  let assemblies = 0;
  // 0) Signature mine carts: one per rail room, on a back wall near the track.
  if (rules.dressingKit.cart > 0) {
    for (const room of [...railRooms].sort((a, b) => a - b)) {
      if (assemblies >= rules.dressingMaxAssemblies) break;
      const distance = (s: Slot): number => Math.min(...railPoints.map((p) => Math.hypot(p.x - s.x, p.z + s.y)));
      const candidates = slots
        .filter((s) => s.room === room && !s.ledge && s.north)
        .map((s) => ({ s, score: distance(s) * 0.2 + s.rank * 0.5 }))
        .sort((a, b) => a.score - b.score || a.s.key - b.s.key)
        .slice(0, D.cartCandidates);
      for (const { s: slot } of candidates) {
        if (slot.used) continue;
        const cluster = tryCluster(slot, false, 'cart');
        if (cluster && commit(slot, cluster, false)) {
          const r = byRoom[room];
          if (r) r.placed += 1;
          assemblies += 1;
          break;
        }
      }
    }
  }
  // 1) Room clusters, round-robin across rooms so the floor cap is shared fairly.
  let progress = true;
  while (progress && assemblies < rules.dressingMaxAssemblies) {
    progress = false;
    for (const [room, r] of byRoom.entries()) {
      if (assemblies >= rules.dressingMaxAssemblies || r.placed >= r.target) continue;
      while (r.cursor < r.list.length) {
        const slot = r.list[r.cursor];
        r.cursor += 1;
        if (slot === undefined || slot.used || clusters.some((d) => !d.filler && d.room === room && Math.hypot(d.x - slot.x, d.z + slot.y) < spacing)) continue;
        const cluster = tryCluster(slot, false);
        if (cluster && commit(slot, cluster, false)) {
          r.placed += 1;
          assemblies += 1;
          progress = true;
          break;
        }
      }
    }
  }
  // 1b) Foreground ledge clusters.
  for (const [i, room] of floor.rooms.entries()) {
    const list = slots.filter((s) => s.ledge && s.room === i && s.rank <= density).sort((a, b) => a.rank - b.rank || a.key - b.key);
    const target = Math.max(1, Math.min(3, Math.round((room.w * rules.dressingLedgePerTilePercent) / 100)));
    let placed = 0;
    for (const slot of list) {
      if (placed >= target || assemblies >= rules.dressingMaxAssemblies) break;
      if (clusters.some((d) => d.ledge && d.room === i && Math.hypot(d.x - slot.x, d.z + slot.y) < spacing)) continue;
      const cluster = tryCluster(slot, false);
      if (cluster && commit(slot, cluster, false)) {
        placed += 1;
        assemblies += 1;
      }
    }
  }
  // 2) Sparse fillers at the remaining wall bases.
  const densityScale = Math.min(1, density / D.referenceDensity);
  for (const slot of slots.filter((s) => !s.used).sort((a, b) => a.rank - b.rank || a.key - b.key)) {
    const chance = tileRandom(dressingSeed, slot.x, slot.y, 90 + (slot.key % 7));
    if (chance > ((slot.room >= 0 ? rules.dressingFillerDensityPercent : rules.dressingCorridorFillerPercent) / 100) * densityScale) continue;
    const cluster = tryCluster(slot, true);
    if (cluster) commit(slot, cluster, true);
  }
  // 3) Low ground scatter near tile corners of room interiors.
  for (let y = 1; y < floor.height - 1; y += 1) {
    for (let x = 1; x < floor.width - 1; x += 1) {
      if (tileAt(x, y) !== 'floor' || roomAt(floor, x, y) < 0 || tileRandom(dressingSeed, x, y, 97) > (rules.dressingScatterDensityPercent / 100) * densityScale) continue;
      const cornerAt = Math.floor(tileRandom(dressingSeed, x, y, 98) * 4);
      const cx = cornerAt & 1 ? 0.5 : -0.5;
      const cy = cornerAt & 2 ? 0.5 : -0.5;
      if (tileAt(x + Math.sign(cx), y) !== 'floor' || tileAt(x, y + Math.sign(cy)) !== 'floor' || tileAt(x + Math.sign(cx), y + Math.sign(cy)) !== 'floor') continue;
      const jx = (tileRandom(dressingSeed, x, y, 99) - 0.5) * D.scatterJitter;
      const jy = (tileRandom(dressingSeed, x, y, 100) - 0.5) * D.scatterJitter;
      const slot: Slot = { scatter: true, x, y, nx: 0, nz: -1, tx: 1, tz: 0, fx: x + cx * D.scatterReach + jx, fz: -y - cy * D.scatterReach - jy, room: roomAt(floor, x, y), key: index(x, y) * 4, rank: 0 };
      const rand = seeded(tileRandom(dressingSeed, x, y, 101));
      const kit = pick<ExtraKit>(
        [
          ['pebbles', kitWeight('rubble') * 0.7],
          ['ore', kitWeight('rubble') * 0.6],
          ['scrap', kitWeight('lumber') * 0.8],
          ['sprig', kitWeight('tufts') * 0.6],
          ['shards', kitWeight('crystals')],
        ],
        rand,
      );
      if (kit === null) continue;
      const placed = realize(slot, kit, rand, 0, 1);
      // Loose plank scraps near the track read as orphan ties: keep them well clear.
      if (kit === 'scrap' && structureGrid.any(placed.bounds, D.scrapRail, (b) => b.rail === true && overlap(placed.bounds, b, D.scrapRail))) continue;
      if (fits(placed.bounds, [], slot)) commit(slot, [placed], true);
    }
  }
  // 3b) Terrace tops near open ground, and chunky silhouettes on camera-side rock.
  const plateau = rules.dressingPlateauDensityPercent / 100;
  const foregroundDensity = rules.dressingForegroundDensityPercent / 100;
  if (plateau > 0) {
    for (const i of [...plateauWalls].sort((a, b) => a - b)) {
      const x = i % floor.width;
      const y = Math.floor(i / floor.width);
      const stub = stubWalls.has(i);
      const walk = (dx: number, dy: number): boolean => isOpen(floor, x + dx, y + dy);
      const foreground = foregroundDensity > 0 && ![1, 2].some((k) => walk(0, -k)) && ![-1, 0, 1].some((dx) => walk(dx, 1)) && [2, 3, 4].some((k) => [-1, 0, 1].some((dx) => walk(dx, k)));
      if (tileRandom(dressingSeed, x, y, 111) > (foreground ? Math.max(plateau, foregroundDensity) : plateau)) continue;
      const rand = seeded(tileRandom(dressingSeed, x, y, 112));
      let brush = 0;
      for (let dy = -2; dy <= 2; dy += 1) for (let dx = -2; dx <= 2; dx += 1) if (walk(dx, dy)) brush = 1;
      const kit =
        foreground && !stub
          ? pick<AnyKit>(
              [
                ['fgCrates', kitWeight('crates')],
                ['fgLumber', kitWeight('lumber')],
                ['fgRocks', kitWeight('rubble') * 1.2],
                ['plateauBrush', kitWeight('tufts') * 0.5 * brush],
              ],
              rand,
            )
          : pick<AnyKit>(
              [
                ['plateauBrush', kitWeight('tufts') * 2.2 * brush],
                ['plateauRocks', kitWeight('rubble') * 1.2],
                ['plateauCrate', stub ? 0 : kitWeight('crates') * 0.35],
                ['plateauBarrel', stub ? 0 : kitWeight('barrels') * 0.25],
                ['crystals', kitWeight('crystals')],
              ],
              rand,
            );
      if (kit === null) continue;
      const slot: Slot = { plateau: true, stub, x, y, nx: 0, nz: -1, tx: 1, tz: 0, fx: x + (rand() - 0.5) * 0.3, fz: -y - 0.2 + (rand() - 0.5) * 0.2, room: -1, fogTile: i, key: i * 4, rank: 0, ...(foreground ? { maxHeight: stub ? D.stubMaxHeight : D.foregroundMaxHeight } : {}) };
      const placed = realize(slot, kit, rand, 0, 1);
      if (fits(placed.bounds, [], slot)) commit(slot, [placed], true);
    }
  }
  // 4) Tiers: each room's first cluster, then other clusters, then fillers; tier caps in triangles.
  const firstOfRoom = (d: Cluster, i: number): boolean => clusters.findIndex((o) => !o.filler && o.room === d.room) === i;
  const order = [...clusters.filter((d, i) => !d.filler && firstOfRoom(d, i)), ...clusters.filter((d, i) => !d.filler && !firstOfRoom(d, i)), ...clusters.filter((d) => d.filler)];
  const shown = [0, 0, 0];
  const kept: Cluster[] = [];
  for (const d of order) {
    const minimum = d.plateau || d.kits.includes('tufts') || d.kits.includes('sprig') ? 2 : d.filler ? 1 : 0;
    d.tier = minimum === 0 && (shown[0] ?? 0) + d.triangles <= D.tierTriangles[0] ? 0 : minimum <= 1 && (shown[1] ?? 0) + d.triangles <= D.tierTriangles[1] ? 1 : 2;
    if (d.tier === 2 && (shown[2] ?? 0) + d.triangles > D.tierTriangles[2]) continue;
    if (d.tier === 0) shown[0] = (shown[0] ?? 0) + d.triangles;
    if (d.tier <= 1) shown[1] = (shown[1] ?? 0) + d.triangles;
    if (d.tier === 2) shown[2] = (shown[2] ?? 0) + d.triangles;
    kept.push(d);
  }
  // Ledge and terrace clusters sit on the rock top under them, and are left out where that would bury them.
  const bounds: Bounds[] = [];
  const triangles: [number, number, number] = [0, 0, 0];
  let keptPieces = 0;
  for (const d of kept) {
    let seat = 0;
    if (d.ledge || d.plateau) {
      const all = union(d.placed.map((k) => k.bounds));
      const samples = [
        [all.x0, all.z0],
        [all.x1, all.z0],
        [all.x0, all.z1],
        [all.x1, all.z1],
        [(all.x0 + all.x1) / 2, (all.z0 + all.z1) / 2],
      ].map(([x, z]) => topAt(x ?? 0, z ?? 0));
      const tops = samples.filter((t): t is number => t !== null);
      if (tops.length === 0) continue;
      seat = Math.min(...tops);
      if (Math.max(...tops) - seat > (d.plateau ? D.plateauSlope : D.ledgeSlope)) continue;
    }
    triangles[d.tier as 0 | 1 | 2] += d.triangles;
    for (const k of d.placed) {
      for (const s of k.specs) {
        parts[s.batch].push(part(s.tile, s.x, s.y + seat, s.z, s.s, s.tint, { rx: s.rx, ry: s.ry, rz: s.rz, cutaway: s.cutaway, mediumOnly: d.tier > 0 }));
        keptPieces += 1;
      }
      bounds.push({ ...k.bounds, y0: k.bounds.y0 + seat, y1: k.bounds.y1 + seat });
    }
  }
  // 5) Foreground kits on the camera-side border apron (Medium only; outside the grid, always in fog).
  if (foregroundDensity > 0) {
    const a = D.apron;
    for (let x = 0, n = 0; x < floor.width && n < a.max; x += a.step) {
      const key = Math.round(x * 5);
      if (tileRandom(dressingSeed + a.salt, key, 0, 131) > Math.min(1, foregroundDensity * a.step)) continue;
      const rand = seeded(tileRandom(dressingSeed + a.salt, key, 0, 132));
      const kit = pick<AnyKit>(
        [
          ['fgCrates', kitWeight('crates')],
          ['fgLumber', kitWeight('lumber')],
        ],
        rand,
      );
      if (kit === null) continue;
      const k: Builder = { pieces: [], rand, colors, low: true, onRock: true };
      KITS[kit](k);
      const fx = x + 0.5 + (rand() - 0.5) * a.jitter;
      const fz = a.near + rand() * a.far;
      for (const piece of k.pieces) {
        const e = euler.setFromQuaternion(piece.q, 'YXZ');
        parts[piece.batch].push(part(-1, fx + piece.p.x, a.top + piece.p.y, fz + piece.p.z - a.back, piece.s, piece.tint, { rx: e.x, ry: e.y, rz: e.z, mediumOnly: true }));
        keptPieces += 1;
      }
      n += 1;
    }
  }
  return { parts, bounds, ledgeWalls, counts: { clusters: kept.length, pieces: keptPieces, triangles } };
};
